/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import {
  GET_PRESENTATION,
  GET_SECTIONS,
  SECTION_OPTION,
  GET_TEMPLATE_COSTUME_TYPES,
  IMPORT_SECTION_OPT_TYPE,
} from "../graphql";
import { VApplication } from "../../../application";

const log: any = LoggerModule?.getInstance?.();

export interface ISectionOptionTypeMethods extends IPluginMethodMap {
  getSectionOptionProps(args?: any): Promise<any>;
  getTemplateCostumeTypes(): Promise<any>;
  importSectionOptType(args: {
    presentation: string;
    templateCostumeType: string;
  }): Promise<any>;
  importCostumeType(args: {
    presentation: string;
    templateCostumeType: string;
  }): Promise<any>;
}

export class SectionOptionTypePageService implements IAgentPlugin {
  readonly methods: ISectionOptionTypeMethods;
  private staticPageContentConfig: any;

  constructor(staticPageContentConfig: any = {}) {
    this.staticPageContentConfig = staticPageContentConfig;

    this.methods = {
      getSectionOptionProps: this.getSectionOptionProps.bind(this),
      getTemplateCostumeTypes: this.getTemplateCostumeTypes.bind(this),
      importSectionOptType: this.importSectionOptType.bind(this),
      importCostumeType: this.importCostumeType.bind(this),
    };
  }

  async getSectionOptionProps(args: any = {}) {
    const app = VApplication.getInstance();
    const graphqlModule = app.getSync(
      "services.GraphqlServerModule",
    ) as GraphqlServerModule;

    const client = graphqlModule?.getClient?.("graphql-gateway");

    const getId = (item: any): string | undefined => {
      if (!item) return undefined;
      if (typeof item === "string") return item;
      return item?.id ?? item?._id;
    };

    const normalizeId = (value: any): string | undefined => {
      if (!value) return undefined;
      if (typeof value === "string") return value;
      return value?.id ?? value?._id;
    };

    const unwrapList = (value: any): any[] => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      if (Array.isArray(value?.content)) return value.content;
      if (Array.isArray(value?.data)) return value.data;
      if (Array.isArray(value?.items)) return value.items;
      if (Array.isArray(value?.nodes)) return value.nodes;
      if (Array.isArray(value?.results)) return value.results;
      return [value];
    };

    const normalizeStatus = (status: any): string =>
      String(status ?? "")
        .trim()
        .toLowerCase();

    const normalizeSectionIds = (value: any): string[] => {
      if (!value) return [];

      if (Array.isArray(value)) {
        return value
          .map((item) => normalizeId(item))
          .filter(Boolean) as string[];
      }

      const id = normalizeId(value);
      return id ? [id] : [];
    };

    const normalizeImages = (
      images?: any,
      img1?: any,
      img2?: any,
    ): string[] => {
      const result: string[] = [];

      if (Array.isArray(images)) {
        images.forEach((image) => {
          if (typeof image === "string" && image) result.push(image);
          if (typeof image === "object" && image?.base64) {
            result.push(image.base64);
          }
        });
      }

      if (typeof img1 === "string" && img1) result.push(img1);
      if (typeof img2 === "string" && img2) result.push(img2);

      return Array.from(new Set(result.filter(Boolean)));
    };

    const getPresentationPayload = (data: any): any =>
      data?.getPresentation ??
      data?.getPresentations ??
      data?.presentations ??
      data?.presentation ??
      null;

    const getCostumeTypePayload = (data: any): any =>
      data?.getCostumeTypes ??
      data?.costumeTypes ??
      data?.sectionOptions ??
      null;

    const normalizeCostumeTypeForSection = (
      costumeType: any,
      sectionId?: string,
      activePresentationId?: string,
    ) => {
      const costumeTypeId = getId(costumeType);

      const displayName = Array.isArray(costumeType?.displayName)
        ? costumeType.displayName.find(
            (item: any) => normalizeId(item?.sectionID) === sectionId,
          )?.name
        : undefined;

      const partition = Array.isArray(costumeType?.partitions)
        ? costumeType.partitions.find(
            (item: any) => normalizeId(item?.sectionID) === sectionId,
          )
        : undefined;

      return {
        ...costumeType,
        id: costumeTypeId,
        baseName: costumeType?.name,
        name:
          displayName || costumeType?.name || "Untitled Section Option Type",
        sectionSpecificInvites: partition?.sectionSpecificInvites ?? 0,
        specialInvites: partition?.specialInvites ?? 0,
        defaultInvites: partition?.defaultInvites ?? 0,

        presentation: costumeType?.presentation ?? activePresentationId,
        section: sectionId,
        sectionId,
        images: normalizeImages(
          costumeType?.images,
          costumeType?.img1,
          costumeType?.img2,
        ),

        costumeFeatures: (costumeType?.costumeFeatures || []).map(
          (feature: any) => {
            const featureId = getId(feature);

            const featureSectionIds =
              normalizeSectionIds(feature?.section).length > 0
                ? normalizeSectionIds(feature?.section)
                : sectionId
                ? [sectionId]
                : [];

            return {
              ...feature,
              id: featureId,
              name: feature?.name ?? "Untitled Feature",
              presentation:
                feature?.presentation ??
                costumeType?.presentation ??
                activePresentationId,
              section: featureSectionIds,
              sections: featureSectionIds,
              costumeType: feature?.costumeType ?? costumeTypeId,
              images: normalizeImages(
                feature?.images,
                feature?.img1,
                feature?.img2,
              ),

              costumeAttributes: (feature?.costumeAttributes || []).map(
                (attribute: any) => {
                  const attributeId = getId(attribute);

                  return {
                    ...attribute,
                    id: attributeId,
                    name: attribute?.name ?? "Untitled Attribute",
                    presentation:
                      attribute?.presentation ??
                      feature?.presentation ??
                      costumeType?.presentation ??
                      activePresentationId,
                    section:
                      normalizeId(attribute?.section) ??
                      featureSectionIds?.[0] ??
                      sectionId,
                    costumeFeature: attribute?.costumeFeature ?? featureId,
                    images: normalizeImages(
                      attribute?.images,
                      attribute?.img1,
                      attribute?.img2,
                    ),

                    costumeValues: (attribute?.costumeValues || []).map(
                      (value: any) => ({
                        ...value,
                        id: getId(value),
                        name: value?.name ?? "Untitled Value",
                        presentation:
                          value?.presentation ??
                          attribute?.presentation ??
                          feature?.presentation ??
                          costumeType?.presentation ??
                          activePresentationId,
                        section:
                          normalizeId(value?.section) ??
                          featureSectionIds?.[0] ??
                          sectionId,
                        costumeAttribute:
                          value?.costumeAttribute ?? attributeId,
                      }),
                    ),
                  };
                },
              ),
            };
          },
        ),
      };
    };

    try {
      if (!client) {
        return {
          isEmpty: true,
          activePresentation: null,
          presentation: null,
          presentationId: undefined,
          sectionId: undefined,
          sections: [],
          sectionOptions: [],
          existingSectionOptionTypes: [],
          staticPageContentConfig: this.staticPageContentConfig ?? {},
          error: "GraphQL client not found.",
        };
      }

      const activePresentationId =
        normalizeId(args?.presentationId) ??
        normalizeId(args?.id) ??
        normalizeId(args?.filter?.presentation?.eq) ??
        normalizeId(args?.presentation);

      let activePresentation = args?.presentation ?? null;

      if (!activePresentation && activePresentationId) {
        try {
          const presentationRes = await client.query({
            query: GET_PRESENTATION,
            variables: {},
            fetchPolicy: "network-only",
          });

          const presentations = unwrapList(
            getPresentationPayload(presentationRes?.data),
          );

          const found = presentations.find(
            (p: any) => normalizeId(p) === activePresentationId,
          );

          if (found) activePresentation = found;
        } catch (err) {
          log?.error?.("error in getPresentation", err);
        }
      }

      if (!activePresentationId) {
        return {
          isEmpty: true,
          activePresentation: null,
          presentation: null,
          presentationId: undefined,
          sectionId: undefined,
          sections: [],
          sectionOptions: [],
          existingSectionOptionTypes: [],
          staticPageContentConfig: this.staticPageContentConfig ?? {},
          message: "No active presentation found.",
        };
      }

      const isAfterUpdate = args?.isAfterUpdate === true;

      const sectionsRes = await client.query({
        query: GET_SECTIONS,
        variables: {
          filter: {
            presentation: {
              eq: activePresentationId,
            },
          },
          options: {
            sort: isAfterUpdate
              ? {
                  field: "updatedAt",
                  order: "desc",
                }
              : {
                  field: "position",
                  order: "asc",
                },
            skip: 0,
            limit: 100,
          },
        },
        fetchPolicy: "network-only",
      });

      const rawSections = unwrapList(sectionsRes?.data?.getSections);

      const sections = rawSections.map((section: any) => {
        const sectionId = normalizeId(section);

        return {
          ...section,
          id: sectionId,
          costumeTypes: (section?.costumeTypes || []).map((costumeType: any) =>
            normalizeCostumeTypeForSection(
              costumeType,
              sectionId,
              activePresentationId,
            ),
          ),
        };
      });

      const sectionOptions = sections.flatMap((section: any) =>
        (section?.costumeTypes || []).map((costumeType: any) => ({
          ...costumeType,
          section: section?.id,
          sectionId: section?.id,
        })),
      );

      const costumeTypeRes = await client.query({
        query: SECTION_OPTION,
        variables: {
          filter: {
            presentation: {
              eq: activePresentationId,
            },
          },
        },
        fetchPolicy: "network-only",
      });

      const costumeTypePayload = getCostumeTypePayload(costumeTypeRes?.data);
      const costumeTypes = unwrapList(costumeTypePayload);

      const existingSectionOptionTypes = (costumeTypes || []).map(
        (costumeType: any) =>
          normalizeCostumeTypeForSection(
            costumeType,
            undefined,
            activePresentationId,
          ),
      );

      return {
        isEmpty: sections.length === 0,
        activePresentation,
        presentation: activePresentation,
        presentationId: activePresentationId,
        sectionId: sections?.[0]?.id,
        sections,
        sectionOptions,
        existingSectionOptionTypes,
        staticPageContentConfig: this.staticPageContentConfig ?? {},
      };
    } catch (err: any) {
      log?.error?.("error in getSectionOptionProps", err);
      console.log("error in getSectionOptionProps", err);

      return {
        isEmpty: true,
        activePresentation: null,
        presentation: null,
        presentationId: undefined,
        sectionId: undefined,
        sections: [],
        sectionOptions: [],
        existingSectionOptionTypes: [],
        staticPageContentConfig: this.staticPageContentConfig ?? {},
        error: err?.message ?? err,
      };
    }
  }

  async getTemplateCostumeTypes() {
    const app = VApplication.getInstance();
    const graphqlModule = app.getSync(
      "services.GraphqlServerModule",
    ) as GraphqlServerModule;

    const client = graphqlModule?.getClient?.("graphql-gateway");

    try {
      if (client) {
        const res = await client.query({
          query: GET_TEMPLATE_COSTUME_TYPES,
          fetchPolicy: "network-only",
        });

        return res?.data?.getTemplateCostumeTypes?.content || [];
      }
    } catch (err: any) {
      const _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);

      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async importSectionOptType(args: {
    presentation: string;
    templateCostumeType: string;
  }) {
    const app = VApplication.getInstance();
    const graphqlModule = app.getSync(
      "services.GraphqlServerModule",
    ) as GraphqlServerModule;

    const client = graphqlModule?.getClient?.("graphql-gateway");

    try {
      if (client) {
        const res = await client.mutate({
          mutation: IMPORT_SECTION_OPT_TYPE,
          variables: {
            filter: {
              presentation: args.presentation,
              templateCostumeType: args.templateCostumeType,
            },
          },
          fetchPolicy: "no-cache",
        });

        return res?.data?.importSectionOptType ?? { success: true };
      }
    } catch (err: any) {
      const _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);

      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async importCostumeType(args: {
    presentation: string;
    templateCostumeType: string;
  }) {
    return this.importSectionOptType(args);
  }
}
